import { useEffect, useRef, useState } from "react";
import { api, fileUrl, money, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CreditCard, AlertTriangle, Building2, Upload, CheckCircle2, Clock, LogOut, FileText } from "lucide-react";

export default function LicenseExpiredGate({ license, onLogout, preview = false, onClose }) {
  const [info, setInfo] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [form, setForm] = useState({ reference: "", amount: "", file_path: "" });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reload = () => api.get("/license/my-proofs").then(({ data }) => setProofs(data)).catch(() => {});
  useEffect(() => {
    api.get("/platform/payment-info").then(({ data }) => setInfo(data)).catch(() => {});
    reload();
    if (license?.total) setForm((s) => ({ ...s, amount: String(license.total) }));
  }, []); // eslint-disable-line

  const pending = proofs.find((p) => p.status === "pending");
  const [mpBusy, setMpBusy] = useState(false);
  const payMp = async () => {
    setMpBusy(true);
    try { const { data } = await api.post("/license/pay/mp"); window.location.assign(data.init_point); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo iniciar el pago"); setMpBusy(false); }
  };
  const hasAccount = info && (info.clabe || info.account_number || info.bank);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/license/upload-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, file_path: data.path }));
      toast.success("Comprobante cargado");
    } catch (e) { toast.error("No se pudo subir el comprobante"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.file_path) return toast.error("Sube tu comprobante de pago");
    setBusy(true);
    try {
      await api.post("/license/payment-proof", { reference: form.reference, amount: Number(form.amount) || 0, file_path: form.file_path, method: "transferencia" });
      toast.success("Comprobante enviado · lo validaremos a la brevedad");
      setForm({ reference: "", amount: "", file_path: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="license-expired-gate">
      <div className="bg-white rounded-2xl max-w-lg w-full my-6 shadow-2xl overflow-hidden">
        <div className="bg-red-500 text-white px-6 py-5 flex items-center gap-3">
          <AlertTriangle size={26} />
          <div>
            <h2 className="font-display font-bold text-xl leading-none">{preview ? "Renovar licencia" : "Tu licencia ha vencido"}</h2>
            <p className="text-sm text-white/90 mt-1">{preview ? `Tu licencia ${license?.billing === "mensual" ? "mensual" : "anual"} vence el ${license?.end_date ? new Date(license.end_date).toLocaleDateString("es-MX") : ""}. Paga ahora para no interrumpir tu operación.` : "Renueva para reactivar tu sistema de punto de venta."}</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {preview && <button type="button" onClick={onClose} className="text-xs text-muted-foreground underline" data-testid="renew-close">Cerrar y seguir trabajando</button>}
          {license?.business_name && <div className="text-sm text-muted-foreground flex items-center gap-1"><Building2 size={14} /> {license.business_name}{license.end_date ? ` · ${preview ? "vence el" : "venció el"} ${new Date(license.end_date).toLocaleDateString("es-MX")}` : ""}</div>}

          <div className="rounded-xl bg-slate-900 text-white px-5 py-4 flex items-center justify-between" data-testid="license-amount-due">
            <div>
              <div className="text-xs text-white/70">Saldo a pagar de tu licencia</div>
              <div className="text-[11px] text-white/50">{license?.max_users || 7} usuarios · {license?.max_stations || 5} estaciones · {license?.billing === "mensual" ? "mensual" : "anual"} (IVA incl.)</div>
            </div>
            <div className="font-mono font-extrabold text-3xl text-emerald-400">{money(license?.total || 0)}</div>
          </div>

          {pending ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-center" data-testid="proof-pending-state">
              <Clock className="mx-auto text-amber-500 mb-2" size={32} />
              <div className="font-bold text-slate-800">Comprobante en revisión</div>
              <p className="text-sm text-muted-foreground mt-1">Recibimos tu comprobante{pending.reference ? ` (ref. ${pending.reference})` : ""}. En cuanto lo validemos, tu licencia se reactivará automáticamente.</p>
              {pending.file_path && <a href={fileUrl(pending.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold mt-2"><FileText size={14} /> Ver comprobante enviado</a>}
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-border p-4">
                {info?.mp_enabled && (
                  <div className="mb-3 rounded-xl border-2 border-sky-400 bg-sky-50 p-3" data-testid="mp-pay-box">
                    <div className="text-sm font-bold text-sky-900 flex items-center gap-2"><CreditCard size={16} /> Pagar con tarjeta (Mercado Pago)</div>
                    <p className="text-xs text-sky-800 mt-0.5">Débito o crédito · la licencia se <b>activa al momento</b> al aprobarse el pago. Importe: <b>{money(license?.total || 0)}</b> ({license?.billing === "mensual" ? "1 mes" : "1 año"}).</p>
                    <Button className="mt-2 w-full bg-sky-600 hover:bg-sky-700 text-white font-bold" disabled={mpBusy} onClick={payMp} data-testid="mp-pay-btn">{mpBusy ? "Redirigiendo a Mercado Pago…" : "Pagar con tarjeta ahora"}</Button>
                  </div>
                )}
                <div className="text-sm font-bold text-slate-700 mb-2">Transferencia bancaria <span className="text-xs font-normal text-muted-foreground">· se activa en 1 hora hábil tras validar tu comprobante</span></div>
                {hasAccount ? (
                  <div className="space-y-1 text-sm">
                    {info.beneficiary && <div className="flex justify-between"><span className="text-muted-foreground">Beneficiario</span><b>{info.beneficiary}</b></div>}
                    {info.bank && <div className="flex justify-between"><span className="text-muted-foreground">Banco</span><b>{info.bank}</b></div>}
                    {info.clabe && <div className="flex justify-between"><span className="text-muted-foreground">CLABE</span><b className="font-mono">{info.clabe}</b></div>}
                    {info.account_number && <div className="flex justify-between"><span className="text-muted-foreground">Cuenta</span><b className="font-mono">{info.account_number}</b></div>}
                    {info.notes && <p className="text-xs text-muted-foreground pt-1 border-t border-border mt-2">{info.notes}</p>}
                  </div>
                ) : (
                  <p className="text-sm text-amber-600">Aún no hay una cuenta de pago configurada. Contacta a soporte para renovar.</p>
                )}
              </div>

              <div className="rounded-xl border border-border p-4 space-y-3">
                <div className="text-sm font-bold text-slate-700">Sube tu comprobante de pago</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Referencia (opcional)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="proof-reference" placeholder="Folio / referencia" /></div>
                  <div><Label className="text-xs">Monto pagado</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="proof-amount" placeholder="0.00" /></div>
                </div>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="proof-file-input" />
                <Button type="button" variant="outline" className="w-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="proof-upload-btn">
                  <Upload size={15} className="mr-1" /> {uploading ? "Subiendo…" : form.file_path ? "Comprobante cargado ✓" : "Seleccionar archivo (imagen o PDF)"}
                </Button>
                {form.file_path && <a href={fileUrl(form.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><FileText size={13} /> Ver archivo</a>}
                <Button onClick={submit} disabled={busy || !form.file_path} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold" data-testid="proof-submit-btn">
                  <CheckCircle2 size={16} className="mr-1" /> Enviar comprobante
                </Button>
              </div>
            </>
          )}

          <Button variant="ghost" className="w-full text-muted-foreground" onClick={onLogout} data-testid="expired-logout"><LogOut size={15} className="mr-1" /> Cerrar sesión</Button>
        </div>
      </div>
    </div>
  );
}
